import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { useExportTracks } from '../hooks/useExportTracks';
import { useAutoScrollPlaylist } from '../hooks/useAutoScrollPlaylist';
import { buildMusicSearchUrl, formatTrackLinks, generateCSV, DEFAULT_MUSIC_SERVICE, MUSIC_SERVICE_LABELS } from '../utils/exportUtils';
import { createSharedPlaylist } from '../api/authClient';
import { trackLinkKey } from '../api/itunesClient';
import { API_BASE } from '../api/config';
import { useAppleMusicSongLinks } from '../hooks/useAppleMusicSongLinks';
import { ZONE_COLORS } from '../galaxy/collisionGraphBuilder';
import MusicServiceIcon from './MusicServiceIcon';
import '../styles/export.css';

const ZONE_LABELS = {
  shared_frontier: 'Shared Frontier',
  your_exploration: 'Incoming Signals',
};

function CollisionPlaylistDrawer({ onClose, collisionNodes, playlistName: defaultName }) {
  const { user, dislikes } = useAuth();
  const { showAuthModal } = useAuthActions();

  const [playlistName, setPlaylistName] = useState(defaultName || 'Stellar: Collision');
  const [linkFormat, setLinkFormat] = useState('with-names');
  const musicService = user?.musicService || DEFAULT_MUSIC_SERVICE;
  const musicServiceLabel = MUSIC_SERVICE_LABELS[musicService] || MUSIC_SERVICE_LABELS[DEFAULT_MUSIC_SERVICE];
  const [copied, setCopied] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareError, setShareError] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const autoplayStartedRef = useRef(false);

  const dislikeNames = useMemo(
    () => new Set((dislikes || []).map((d) => d.artistName)),
    [dislikes]
  );

  // Build zone lookup for badge rendering
  const nodeZoneMap = useMemo(() => {
    const map = new Map();
    for (const node of collisionNodes) {
      map.set(node.id, node.zone);
    }
    return map;
  }, [collisionNodes]);

  // Batch-fetch tracks for collision nodes
  const { tracks, progress, isLoading, refetchTrack } = useExportTracks(collisionNodes);

  const trackList = useMemo(() => {
    const list = [];
    for (const node of collisionNodes) {
      const track = tracks.get(node.id);
      if (track) list.push({ ...track, nodeId: node.id });
    }
    return list;
  }, [collisionNodes, tracks]);

  const trackListRef = useRef(trackList);
  trackListRef.current = trackList;
  const autoplayRef = useRef(autoplay);
  autoplayRef.current = autoplay;
  const audioInstanceRef = useRef(null);

  const findNextPreview = useCallback((fromIdx, direction = 1, wrap = true) => {
    const list = trackListRef.current;
    const len = list.length;
    if (len === 0) return -1;
    for (let step = 1; step < len; step++) {
      const i = ((fromIdx + step * direction) % len + len) % len;
      if (list[i].previewUrl) return i;
      if (!wrap && (direction > 0 ? i >= len - 1 : i <= 0)) break;
    }
    return -1;
  }, []);

  const handleTrackEnded = useCallback(() => {
    if (!autoplayRef.current || !autoplayStartedRef.current) return;
    const list = trackListRef.current;
    const currentId = audioInstanceRef.current?.currentTrack?.id;
    const currentIdx = list.findIndex((t) => t.id === currentId);
    if (currentIdx === -1) return;
    for (let i = currentIdx + 1; i < list.length; i++) {
      if (list[i].previewUrl) {
        audioInstanceRef.current?.play(list[i]);
        return;
      }
    }
  }, []);

  const handleNext = useCallback(() => {
    const list = trackListRef.current;
    const currentId = audioInstanceRef.current?.currentTrack?.id;
    const currentIdx = list.findIndex((t) => t.id === currentId);
    if (currentIdx === -1) return;
    const next = findNextPreview(currentIdx, 1);
    if (next !== -1) audioInstanceRef.current?.play(list[next]);
  }, [findNextPreview]);

  const handlePrev = useCallback(() => {
    const list = trackListRef.current;
    const currentId = audioInstanceRef.current?.currentTrack?.id;
    const currentIdx = list.findIndex((t) => t.id === currentId);
    if (currentIdx === -1) return;
    const prev = findNextPreview(currentIdx, -1);
    if (prev !== -1) audioInstanceRef.current?.play(list[prev]);
  }, [findNextPreview]);

  // Re-fetch a track's data when its preview URL fails (e.g. expired CDN
  // link after ~15 min).  Returns a fresh track object or null.
  const handlePlaybackError = useCallback(async (failedTrack) => {
    if (!failedTrack?.nodeId) return null;
    const fresh = await refetchTrack(failedTrack.nodeId);
    if (fresh) return { ...fresh, nodeId: failedTrack.nodeId };
    return null;
  }, [refetchTrack]);

  const audio = useAudioPreview({
    onEnded: handleTrackEnded,
    onNext: handleNext,
    onPrev: handlePrev,
    onPlaybackError: handlePlaybackError,
    mediaSession: true,
  });
  audioInstanceRef.current = audio;

  // Skip to next preview when the currently-playing artist is disliked
  const prevDislikeNamesRef = useRef(dislikeNames);
  useEffect(() => {
    const prev = prevDislikeNamesRef.current;
    prevDislikeNamesRef.current = dislikeNames;
    const currentTrack = audioInstanceRef.current?.currentTrack;
    if (!audioInstanceRef.current?.isPlaying || !currentTrack?.artistName) return;
    if (dislikeNames.has(currentTrack.artistName) && !prev.has(currentTrack.artistName)) {
      handleNext();
    }
  }, [dislikeNames, handleNext]);

  // Auto-scroll the tracklist to keep the playing track visible
  const tracklistRef = useRef(null);
  const activeTrackId = audio.isPlaying ? audio.currentTrack?.id ?? null : null;
  useAutoScrollPlaylist(tracklistRef, activeTrackId);

  // Canonical Apple Music links, filled in as background lookups resolve
  const appleMusicLinks = useAppleMusicSongLinks(trackList, musicService);

  // Track list with each track's outbound link attached — canonical Apple
  // Music URLs where resolved, search URLs otherwise. Feeds the share bake,
  // link copy, and CSV export so they carry the best link known at the time.
  const linkedTracks = useMemo(
    () => trackList.map((t) => ({
      ...t,
      musicUrl: appleMusicLinks.get(trackLinkKey(t)) || buildMusicSearchUrl(t, musicService),
    })),
    [trackList, appleMusicLinks, musicService]
  );

  const handleCopyLinks = useCallback(async () => {
    if (linkedTracks.length === 0) return;
    const text = formatTrackLinks(linkedTracks, linkFormat, musicService);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [linkedTracks, linkFormat, musicService]);

  const handleExportCSV = useCallback(() => {
    if (linkedTracks.length === 0) return;
    const csv = generateCSV(linkedTracks, musicService);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${playlistName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [linkedTracks, playlistName, musicService]);

  const handleShare = useCallback(async () => {
    if (!user) {
      showAuthModal('login');
      return;
    }
    if (linkedTracks.length === 0) return;

    setSharing(true);
    setShareError(null);
    try {
      const shareTrackList = linkedTracks.map((t) => ({
        name: t.name,
        artistName: t.artistName,
        albumName: t.albumName,
        albumImage: t.albumImage,
        previewUrl: t.previewUrl,
        durationMs: t.durationMs,
        deezerUrl: t.externalUrl,
        musicUrl: t.musicUrl,
      }));

      const res = await createSharedPlaylist({
        playlistName,
        trackList: shareTrackList,
        seedArtists: [],
        nodeCount: collisionNodes.length,
        musicService,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Share failed');

      // In the Capacitor iOS app window.location.origin is capacitor://localhost,
      // which navigator.share rejects (non-http URL) and no one else can open —
      // use the public origin baked into API_BASE instead.
      const shareBase = API_BASE || window.location.origin;
      const fullUrl = `${shareBase}/p/${data.id}`;

      // Always show the URL row — navigator.share can reject (user cancel,
      // or the user-activation window expiring during the network call
      // above), so it must not gate the fallback UI.
      setShareUrl(fullUrl);
      if (navigator.share) {
        // Mobile/iOS: also open the native share sheet
        try {
          await navigator.share({
            title: playlistName,
            text: `Check out this collision playlist: ${playlistName}`,
            url: fullUrl,
          });
        } catch {}
      } else {
        try { await navigator.clipboard.writeText(fullUrl); } catch {}
      }
    } catch (err) {
      console.warn('Share failed:', err.message);
      setShareError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSharing(false);
    }
  }, [user, showAuthModal, linkedTracks, playlistName, collisionNodes.length, musicService]);

  const handlePreviewToggle = useCallback(
    (track) => {
      if (audio.isPlaying && audio.currentTrack?.id === track.id) {
        audio.pause();
      } else {
        audio.play(track);
        autoplayStartedRef.current = true;
      }
    },
    [audio]
  );

  function getZoneBadge(nodeId) {
    const zone = nodeZoneMap.get(nodeId);
    if (!zone) return null;
    const color = ZONE_COLORS[zone];
    if (!color) return null;
    return (
      <span
        className="collision-playlist-zone-badge"
        style={{ '--zone-hue': color.h, '--zone-sat': `${color.s}%`, '--zone-light': `${color.l}%` }}
        title={ZONE_LABELS[zone] || zone}
      >
        <span className="collision-playlist-zone-dot" />
        <span className="collision-playlist-zone-label">{ZONE_LABELS[zone] || zone}</span>
      </span>
    );
  }

  return (
    <div className="export-drawer">
      <button className="export-drawer-close" onClick={onClose}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="export-drawer-content">
        {/* Header */}
        <div className="export-drawer-header">
          <h3 className="export-drawer-title">Collision Playlist</h3>
          <input
            className="export-playlist-name-input"
            type="text"
            value={playlistName}
            onChange={(e) => setPlaylistName(e.target.value)}
            maxLength={100}
          />
          <div className="export-meta-row">
            <span className="export-track-count">
              {isLoading
                ? `Loading tracks... ${progress.fetched}/${progress.total}`
                : `${trackList.length} track${trackList.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        {isLoading && (
          <div className="export-progress-bar">
            <div
              className="export-progress-fill"
              style={{ width: `${progress.total > 0 ? (progress.fetched / progress.total) * 100 : 0}%` }}
            />
          </div>
        )}

        {/* Autoplay toggle */}
        <div className="export-autoplay-filter">
          <button
            className={`export-dislike-toggle ${autoplay ? 'active' : ''}`}
            onClick={() => setAutoplay((v) => !v)}
            aria-label="Toggle autoplay"
          />
          <span className="export-dislike-label">Autoplay</span>
          <span className="export-autoplay-hint">
            {autoplay ? 'Plays next after preview ends' : 'Off'}
          </span>
        </div>

        {/* Actions */}
        <div className="export-actions">
          <button
            className="export-action-btn primary"
            onClick={handleShare}
            disabled={trackList.length === 0 || isLoading || sharing}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            {sharing ? 'Creating link...' : 'Share Link'}
          </button>

          {shareError && (
            <div className="export-share-error">{shareError}</div>
          )}

          {shareUrl && (
            <div className="export-share-url">
              <input
                className="export-share-url-input"
                type="text"
                value={shareUrl}
                readOnly
                onClick={(e) => e.target.select()}
              />
              <button
                className="export-share-url-copy"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(shareUrl); } catch {}
                  setShareLinkCopied(true);
                  setTimeout(() => setShareLinkCopied(false), 2000);
                }}
                title="Copy link"
              >
                {shareLinkCopied ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" width="14" height="14">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
              <a className="export-share-url-open" href={shareUrl} target="_blank" rel="noopener noreferrer">
                Open
              </a>
            </div>
          )}

          <button
            className="export-action-btn export-action-btn-csv"
            onClick={handleExportCSV}
            disabled={trackList.length === 0 || isLoading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>

          <button
            className="export-action-btn export-action-btn-copy"
            onClick={handleCopyLinks}
            disabled={trackList.length === 0 || isLoading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {copied ? 'Copied!' : `Copy ${musicServiceLabel} Links`}
          </button>

          <div className="export-link-format">
            <button
              className={linkFormat === 'links-only' ? 'active' : ''}
              onClick={() => setLinkFormat('links-only')}
            >
              Links Only
            </button>
            <button
              className={linkFormat === 'with-names' ? 'active' : ''}
              onClick={() => setLinkFormat('with-names')}
            >
              With Names
            </button>
          </div>
        </div>

        {/* Tracklist */}
        <div className="export-tracklist" ref={tracklistRef}>
          {collisionNodes.reduce((rows, node) => {
            const track = tracks.get(node.id);

            if (!track) {
              // While still loading, show skeleton placeholders.
              // Once loading is done, gracefully hide tracks that
              // failed to load instead of leaving blank gaps.
              if (isLoading) {
                rows.push(
                  <div key={node.id} className="export-track-skeleton">
                    <span className="export-track-number">{rows.length + 1}</span>
                    <div className="export-skeleton-art" />
                    <div className="export-skeleton-text">
                      <div className="export-skeleton-line" style={{ width: '80%' }} />
                      <div className="export-skeleton-line short" />
                    </div>
                  </div>
                );
              }
              return rows;
            }

            const isCurrentlyPlaying = audio.isPlaying && audio.currentTrack?.id === track.id;
            const displayNum = rows.length + 1;

            rows.push(
              <div key={node.id} className={`export-track-row${isCurrentlyPlaying ? ' active' : ''}`} data-track-id={track.id}>
                <span className="export-track-number">{displayNum}</span>
                {track.albumImage ? (
                  <img className="export-track-art" src={track.albumImage} alt="" loading="lazy" />
                ) : (
                  <div className="export-track-art-placeholder" />
                )}
                <div className="export-track-info">
                  <span className="export-track-name">{track.name}</span>
                  <span className="export-track-artist">
                    {track.artistName}
                  </span>
                  {getZoneBadge(node.id)}
                </div>
                {track.previewUrl && (
                  <button
                    className={`export-track-preview-btn ${isCurrentlyPlaying ? 'playing' : ''}`}
                    onClick={() => handlePreviewToggle(track)}
                    title={isCurrentlyPlaying ? 'Pause preview' : 'Play preview'}
                  >
                    {isCurrentlyPlaying ? (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                        <polygon points="6,3 20,12 6,21" />
                      </svg>
                    )}
                    {audio.currentTrack?.id === track.id && audio.progress > 0 && (
                      <svg className="export-preview-progress-ring" viewBox="0 0 32 32">
                        <circle cx="16" cy="16" r="14" fill="none" stroke="rgba(129, 140, 248, 0.25)" strokeWidth="2" />
                        <circle
                          cx="16" cy="16" r="14"
                          fill="none"
                          stroke="#818cf8"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 14}
                          strokeDashoffset={2 * Math.PI * 14 * (1 - (audio.progress || 0))}
                        />
                      </svg>
                    )}
                  </button>
                )}
                <a
                  className={`export-track-spotify-link${musicService === 'apple-music' ? ' apple-music' : ''}`}
                  href={appleMusicLinks.get(trackLinkKey(track)) || buildMusicSearchUrl(track, musicService)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Search in ${musicServiceLabel}`}
                >
                  <MusicServiceIcon service={musicService} size={19} />
                </a>
              </div>
            );
            return rows;
          }, [])}

          {!isLoading && trackList.length === 0 && (
            <div className="export-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32" style={{ color: 'rgba(255,255,255,0.2)' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              <span className="export-empty-text">No tracks available</span>
            </div>
          )}
        </div>
      </div>

      {copied && <div className="export-toast">Copied to clipboard!</div>}
    </div>
  );
}

export default CollisionPlaylistDrawer;
